import { IconButton } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { AlertCircleFilled, CheckmarkCircle, CheckmarkCircleFilled } from '@creditornot/cb-icons';
import { colors } from '@creditornot/cb-ingredients';
import { Input, Textarea } from '@creditornot/cb-input';
import { Popover } from '@creditornot/cb-popover';
import React, { useEffect, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Translation, MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { Typography as T } from 'common/typography';

import { TranslationSectionItem } from '../types';
import * as utils from '../utils';

export type Props = {
  menuData: MenuContainer;
  item: TranslationSectionItem;
  language: Language;
  primaryLanguage: Language;
  isRightSide: boolean;
  isError: boolean;
  isPending: boolean;
  onSaveTranslation: (data: { value: string; item: TranslationSectionItem; pos: 'right' | 'left' }) => void;
};

const Root = styled.div`
  width: 100%;
  position: relative;
  margin-block-start: 8px;
`;

const inputMixin = css<{ isOriginalChanged: boolean }>`
  & > input,
  & > textarea {
    background-color: ${({ isOriginalChanged }) => (isOriginalChanged ? colors.wolt12 : 'none')};
  }
`;

const NameInput = styled(Input)<{ isOriginalChanged: boolean }>`
  ${inputMixin}
`;

const DescriptionTextArea = styled(Textarea)<{ isOriginalChanged: boolean }>`
  ${inputMixin}
`;

const ProductInfoTextArea = styled(Textarea)<{ isOriginalChanged: boolean }>`
  ${inputMixin}
`;

const IconWrapper = styled.div<{ isRightSize: boolean }>`
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  ${({ isRightSize }) =>
    isRightSize
      ? css`
          left: 100%;
          margin-inline-start: 10px;
        `
      : css`
          right: 100%;
          margin-inline-end: 10px;
        `}
`;

const AlertIcon = styled(AlertCircleFilled)`
  width: 20px;
  height: auto;
  margin-block-start: 7px;
  fill: ${colors.strawberry100};
`;

const TooltipTitle = styled(T.Title1)`
  display: block;
  margin-block-end: 8px;
`;

const TooltipContent = styled(T.Body3)`
  display: block;
  padding: 10px;
  max-width: 300px;
`;

const TranslationInput = ({
  menuData,
  item,
  language,
  primaryLanguage,
  isRightSide,
  isError,
  isPending,
  onSaveTranslation,
}: Props) => {
  const { menu, items, options } = menuData;
  const { itemType, field, itemId, parentId } = item;
  const element = utils.findMenuElement(itemType, itemId, parentId, menu, items, options);

  const translations = element && ((element as any)[field] as Translation[]);
  const primaryLanguageTranslation = translations?.find(({ lang }) => lang === primaryLanguage);

  const translation = translations?.find(({ lang }) => lang === language);

  const placeholder = useMemo(() => {
    if (language === primaryLanguage) {
      return '';
    }
    return primaryLanguageTranslation?.value;
  }, [language, primaryLanguage, primaryLanguageTranslation?.value]);

  const text = useMemo(() => {
    if (language === primaryLanguage) {
      return primaryLanguageTranslation?.value || '';
    }
    return translation ? translation.value : '';
  }, [language, primaryLanguage, primaryLanguageTranslation?.value, translation]);

  const isOriginalChanged = useMemo(() => {
    if (language === primaryLanguage) {
      return false;
    }
    return translation ? !translation.verified : false;
  }, [language, primaryLanguage, translation]);

  const fieldMaxLength = utils.getFieldMaxLength(itemType, field);

  const [inputValue, setInputValue] = useState(text);

  useEffect(() => {
    setInputValue(text);
  }, [text]);

  const handleInputChangeEvent: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = e => {
    const newValue = e.currentTarget.value;
    setInputValue(fieldMaxLength === null ? newValue : newValue.substring(0, fieldMaxLength));
  };

  const handleVerifyClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault();
    if (inputValue === text) {
      onSaveTranslation({ value: text, item, pos: isRightSide ? 'right' : 'left' });
    }
  };

  const handleBlur = () => {
    if (inputValue !== text) {
      onSaveTranslation({ value: inputValue, item, pos: isRightSide ? 'right' : 'left' });
    }
  };

  const handleInputBlurEvent: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> = e => {
    e.preventDefault();
    handleBlur();
  };

  return (
    <Root>
      <InputContainer characterCount={{ value: inputValue.length, max: fieldMaxLength }}>
        {field === 'name' && (
          <NameInput
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChangeEvent}
            onBlur={handleInputBlurEvent}
            isOriginalChanged={isOriginalChanged}
            invalid={isError}
            disabled={isPending}
          />
        )}
        {field === 'description' && (
          <DescriptionTextArea
            disableHorizontalResize
            disableVerticalResize
            rows={5}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChangeEvent}
            onBlur={handleInputBlurEvent}
            isOriginalChanged={isOriginalChanged}
            invalid={isError}
            disabled={isPending}
          />
        )}
        {utils.isProductInfoField(field) && (
          <ProductInfoTextArea
            disableHorizontalResize
            disableVerticalResize
            rows={5}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChangeEvent}
            onBlur={handleBlur}
            isOriginalChanged={isOriginalChanged}
            invalid={isError}
            disabled={isPending}
          />
        )}
      </InputContainer>
      {isError && (
        <IconWrapper isRightSize={isRightSide}>
          <Popover
            content={
              <TooltipContent>
                <TooltipTitle>
                  <FormattedMessage id="translation.errorTooltipTitle" />
                </TooltipTitle>
                <FormattedMessage id="translation.errorTooltipBody" />
              </TooltipContent>
            }
            renderArrow={false}
            showOnHover
            disableContentPadding
          >
            <AlertIcon />
          </Popover>
        </IconWrapper>
      )}
      {isOriginalChanged && (
        <IconWrapper isRightSize={isRightSide}>
          <IconButton size="small" variant="blue" onClick={handleVerifyClick}>
            {inputValue !== text ? <CheckmarkCircleFilled /> : <CheckmarkCircle />}
          </IconButton>
        </IconWrapper>
      )}
    </Root>
  );
};

export default TranslationInput;
